import React from 'react';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {useTranslations} from '../../hooks/use-translations';
import {UsedVehicle} from '../../typing/used-inventory';
import {DEFAULT_FILTERS_CONFIG} from '../../utils/app-constants';
import {separateThousand} from '../../utils/general';
import ShowMoreText from '../show-more-text';
import {
  StyledColorDot,
  StyledColorWrapper,
  StyledSpec,
  StyledSpecs,
  StyledSpecHeading,
  StyledSpecsWrapper,
  StyledSpecText,
  StyledDescriptionContainer,
} from './styles';

interface CarDetailedSpecsProps {
  readonly vehicle?: UsedVehicle;
}

const CarDetailedSpecs: React.FC<CarDetailedSpecsProps> = props => {
  const {vehicle} = props;
  const translations = useTranslations().detailsSection.DetailedSpecs;
  let {filterConfig = DEFAULT_FILTERS_CONFIG} = useFeatureAppConfig();
  const contentRef = React.useRef<HTMLDivElement>(null);
  filterConfig = {...DEFAULT_FILTERS_CONFIG, ...filterConfig};
  const vehicleColor =
    vehicle?.exteriorColorBaseColor &&
    filterConfig.exterior.colorMap[vehicle?.exteriorColorBaseColor];
  const fuelType =
    vehicle?.engineFuelType &&
    filterConfig.fuelType.typeMap[vehicle?.engineFuelType];
  const descriptionItems:
    | string[]
    | undefined = vehicle?.standardFeatures?.split('|');

  return vehicle ? (
    <StyledSpecsWrapper ref={contentRef} className="detailed-specs-wrapper">
      <StyledSpecs className="detailed-specs">
        {vehicle?.odometer && (
          <StyledSpec className="detailed-spec detailed-spec-mileage">
            <StyledSpecHeading className="spec-heading">
              {translations.mileageHeading}
            </StyledSpecHeading>
            <StyledSpecText className="spec-text">
              {separateThousand(`${vehicle.odometer}`)} {translations.miles}
            </StyledSpecText>
          </StyledSpec>
        )}
        {vehicle?.engineDescription && fuelType && (
          <StyledSpec className="detailed-spec detailed-spec-engine">
            <StyledSpecHeading className="spec-heading">
              {translations.engineHeading}
            </StyledSpecHeading>
            <StyledSpecText className="spec-text">
              <span className="notranslate">{vehicle.engineDescription}</span>
              <span>{fuelType}</span>
            </StyledSpecText>
          </StyledSpec>
        )}
        {vehicle?.transmissionDescription && (
          <StyledSpec className="detailed-spec detailed-spec-transmission">
            <StyledSpecHeading className="spec-heading">
              {translations.transmissionHeading}
            </StyledSpecHeading>
            <StyledSpecText className="spec-text">
              {vehicle?.transmissionDescription}
            </StyledSpecText>
          </StyledSpec>
        )}
        {vehicle?.exteriorColorBaseColor && vehicleColor && (
          <StyledSpec className="detailed-spec detailed-spec-color">
            <StyledSpecHeading className="spec-heading spec-color">
              {translations.colorHeading}
            </StyledSpecHeading>
            <StyledSpecText className="spec-text">
              <StyledColorWrapper className="color-wrapper">
                <StyledColorDot
                  className="color-dot"
                  colorCode={vehicleColor}
                />
                <span className="color-name">
                  {vehicle.exteriorColorBaseColor}
                </span>
              </StyledColorWrapper>
            </StyledSpecText>
          </StyledSpec>
        )}
      </StyledSpecs>
      {descriptionItems && (
        <StyledDescriptionContainer className="description-wrapper">
          <StyledSpecHeading className="spec-heading spec-description">
            {translations.descriptionHeading}
          </StyledSpecHeading>
          <ShowMoreText
            items={descriptionItems}
            ref={contentRef}
            vehicleData={vehicle}
          />
        </StyledDescriptionContainer>
      )}
    </StyledSpecsWrapper>
  ) : (
    <></>
  );
};

export default CarDetailedSpecs;
